/**
 * Levantamentos — fila de pagamentos aos trabalhadores.
 *
 * Nenhum gateway angolano faz pagamentos a terceiros, por isso a saída é
 * executada aqui: a equipa vê o pedido, faz a transferência no banco (ou gera
 * uma referência de levantamento sem cartão) e regista o comprovativo. Só
 * então o saldo do trabalhador baixa de vez.
 *
 * O IBAN só é revelado quando é preciso pagar, e esse acesso fica em auditoria.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Wallet, Eye, Check, X, Loader2, ShieldCheck, Banknote, Activity } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../hooks/useToast';
import ContaDoExecutante from '../components/ContaDoExecutante';
import '../styles/WithdrawalsPage.css';

const kz = (v) => `Kz ${Number(v || 0).toLocaleString('pt-PT')}`;

const ESTADOS = {
  pending:    { txt: 'Em espera',   cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  processing: { txt: 'A processar', cls: 'bg-purple-500/15 text-purple-400 border-purple-500/30' },
  paid:       { txt: 'Pago',        cls: 'bg-green-500/15 text-green-400 border-green-500/30' },
  rejected:   { txt: 'Rejeitado',   cls: 'bg-red-500/15 text-red-400 border-red-500/30' },
};

const WithdrawalsPage = () => {
  const { success, error } = useToast();
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('pending');
  const [dados, setDados] = useState(null);       // dados bancários revelados
  const [aPagar, setAPagar] = useState(null);     // levantamento em pagamento
  const [referencia, setReferencia] = useState('');
  const [notas, setNotas] = useState('');
  const [busy, setBusy] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/withdrawals', { params: filtro ? { estado: filtro } : {} });
      setLista(Array.isArray(data) ? data : []);
    } catch {
      error('Erro ao carregar levantamentos');
    } finally {
      setLoading(false);
    }
  }, [filtro, error]);

  useEffect(() => { carregar(); }, [carregar]);

  const verDados = async (w) => {
    try {
      const { data } = await api.get(`/withdrawals/${w.id}/dados-pagamento`);
      setDados({ ...data, trabalhador: w.trabalhador });
    } catch {
      error('Erro ao obter os dados de pagamento');
    }
  };

  const pagar = async () => {
    if (!referencia.trim()) { error('Indique a referência ou o comprovativo'); return; }
    setBusy(true);
    try {
      await api.post(`/withdrawals/${aPagar.id}/pagar`, { referencia: referencia.trim(), notas });
      success('Levantamento marcado como pago');
      setAPagar(null); setReferencia(''); setNotas(''); setDados(null);
      carregar();
    } catch (e) {
      error(e?.response?.data?.message || 'Erro ao marcar como pago');
    } finally { setBusy(false); }
  };

  const rejeitar = async (w) => {
    const motivo = window.prompt('Motivo da rejeição (o profissional vai vê-lo):');
    if (!motivo) return;
    try {
      await api.post(`/withdrawals/${w.id}/rejeitar`, { motivo });
      success('Levantamento rejeitado');
      carregar();
    } catch (e) {
      error(e?.response?.data?.message || 'Erro ao rejeitar');
    }
  };

  const totalVisivel = lista.reduce((acc, w) => acc + Number(w.valor || 0), 0);
  const filtroAtual = {
    pending: 'Em espera', processing: 'A processar', paid: 'Pagos', rejected: 'Rejeitados', '': 'Todos',
  }[filtro];

  return (
    <div className="withdrawals-admin-page space-y-6">
      <section className="withdrawals-admin-hero">
        <div>
          <span className="withdrawals-admin-eyebrow"><ShieldCheck className="w-4 h-4" /> Operação financeira segura</span>
          <h1>Levantamentos</h1>
          <p>Valide pagamentos aos profissionais, consulte dados protegidos e mantenha cada transferência auditável.</p>
        </div>
        <div className="withdrawals-queue-summary">
          <Banknote />
          <span><small>{filtroAtual}</small><strong>{kz(totalVisivel)}</strong><i>{lista.length} {lista.length === 1 ? 'pedido' : 'pedidos'} na lista</i></span>
        </div>
      </section>

      <div className="withdrawals-filter-shell">
        <span><Activity className="w-4 h-4" /> Estado da operação</span>
        <div>
        {[
          { v: 'pending', t: 'Em espera' },
          { v: 'processing', t: 'A processar' },
          { v: 'paid', t: 'Pagos' },
          { v: 'rejected', t: 'Rejeitados' },
          { v: '', t: 'Todos' },
        ].map(o => (
          <button key={o.v} onClick={() => setFiltro(o.v)}
            className={filtro === o.v ? 'is-active' : ''}>
            {o.t}
          </button>
        ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 py-10 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> A carregar…
        </div>
      ) : lista.length === 0 ? (
        <div className="withdrawals-empty">
          <Wallet />
          <strong>Nenhum levantamento</strong>
          <span>Não existem pedidos no estado selecionado.</span>
        </div>
      ) : (
        <div className="withdrawals-list">
          {lista.map(w => {
            const e = ESTADOS[w.estado] || ESTADOS.pending;
            const semCartao = w.metodo === 'levantamento_sem_cartao';
            return (
              <article key={w.id} className="withdrawal-card">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Quem recebe é a empresa, quando o dinheiro é dela: é
                          o NIF dela que está na factura, e é para ela que a
                          transferência tem de ir. A pessoa aparece a seguir,
                          como quem pediu. */}
                      <span className="text-white font-semibold">
                        {w.empresa?.nome || w.trabalhador?.nome}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${e.cls}`}>{e.txt}</span>
                    </div>
                    {w.empresa && (
                      <div className="text-cyan-400 text-xs mt-0.5">
                        NIF {w.empresa.nif || '—'} · pedido por {w.trabalhador?.nome}
                      </div>
                    )}
                    <div className="text-gray-500 text-xs mt-0.5">
                      {semCartao
                        ? `Levantamento sem cartão · ${w.telefone_levantamento || w.trabalhador?.telefone || '—'}`
                        : `Transferência · ${w.banco || 'banco não indicado'} ${w.iban_last4 ? '· ····' + w.iban_last4 : ''}`}
                      {' · '}{new Date(w.pedido_em).toLocaleString('pt-PT')}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-green-400 tabular-nums">{kz(w.valor)}</div>
                  </div>
                </div>

                <ContaDoExecutante levantamento={w} />

                {['pending', 'processing'].includes(w.estado) && (
                  <div className="flex gap-2 mt-3 flex-wrap">
                    <button onClick={() => verDados(w)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-800 text-gray-300 border border-gray-700 hover:text-white">
                      <Eye className="w-3.5 h-3.5" /> Ver dados para pagar
                    </button>
                    <button onClick={() => { setAPagar(w); setReferencia(''); setNotas(''); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-600 text-white hover:bg-green-500">
                      <Check className="w-3.5 h-3.5" /> Marcar como pago
                    </button>
                    <button onClick={() => rejeitar(w)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-transparent text-red-400 border border-red-500/40 hover:bg-red-500/10">
                      <X className="w-3.5 h-3.5" /> Rejeitar
                    </button>
                  </div>
                )}

                {w.estado === 'rejected' && w.motivo_rejeicao && (
                  <div className="mt-2 text-xs text-red-400">{w.motivo_rejeicao}</div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {/* Dados bancários revelados (acesso auditado) */}
      {dados && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setDados(null)}>
          <div className="withdrawals-modal max-w-md w-full" onClick={(ev) => ev.stopPropagation()}>
            <h3 className="text-white font-bold mb-1">Dados para pagamento</h3>
            <p className="text-gray-500 text-xs mb-4">Este acesso fica registado em auditoria.</p>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3"><dt className="text-gray-500">Profissional</dt><dd className="text-white">{dados.trabalhador?.nome}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-gray-500">Valor</dt><dd className="text-green-400 font-bold">{kz(dados.valor)}</dd></div>
              {dados.metodo === 'conta_bancaria' ? (
                <>
                  <div className="flex justify-between gap-3"><dt className="text-gray-500">Titular</dt><dd className="text-white">{dados.titular || '—'}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-gray-500">Banco</dt><dd className="text-white">{dados.banco || '—'}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-gray-500">IBAN</dt><dd className="text-white font-mono text-xs break-all">{dados.iban || '—'}</dd></div>
                </>
              ) : (
                <>
                  <div className="flex justify-between gap-3"><dt className="text-gray-500">Método</dt><dd className="text-white">Levantamento sem cartão</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-gray-500">Telemóvel</dt><dd className="text-white">{dados.telefone || '—'}</dd></div>
                  <p className="text-gray-500 text-xs pt-2">
                    Gere a referência de levantamento sem cartão no banco e registe-a ao marcar como pago —
                    o profissional vê-a na app e levanta no ATM.
                  </p>
                </>
              )}
            </dl>
            <button onClick={() => setDados(null)} className="mt-5 w-full py-2 rounded-lg bg-gray-800 text-gray-300 text-sm hover:text-white">Fechar</button>
          </div>
        </div>
      )}

      {/* Marcar como pago */}
      {aPagar && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setAPagar(null)}>
          <div className="withdrawals-modal max-w-md w-full" onClick={(ev) => ev.stopPropagation()}>
            <h3 className="text-white font-bold mb-1">Marcar como pago</h3>
            <p className="text-gray-500 text-xs mb-4">
              {aPagar.metodo === 'levantamento_sem_cartao'
                ? 'Escreva a referência de levantamento — é o que o profissional vai usar no ATM.'
                : 'Escreva o comprovativo/referência da transferência.'}
            </p>
            <input value={referencia} onChange={(ev) => setReferencia(ev.target.value)}
              placeholder={aPagar.metodo === 'levantamento_sem_cartao' ? 'Referência de levantamento' : 'Referência da transferência'}
              className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm mb-2" />
            <textarea value={notas} onChange={(ev) => setNotas(ev.target.value)} rows={2}
              placeholder="Notas internas (opcional)"
              className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm" />
            <div className="flex gap-2 mt-4">
              <button onClick={pagar} disabled={busy}
                className="flex-1 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-500 disabled:opacity-60">
                {busy ? 'A guardar…' : 'Confirmar pagamento'}
              </button>
              <button onClick={() => setAPagar(null)} className="px-4 py-2 rounded-lg bg-gray-800 text-gray-300 text-sm">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawalsPage;
